import type { ContestEntryDTO, ContestResponse } from '@shared/protocol/messages';
import { RULES, skillDef } from '../../combat/rules';
import { CONTEST_VOTE_COINS, claimChampionPrizes, contestCandidates, hallMonsterId, rewardFirstVote, syncHall } from '../../systems/contest';
import { monsterArt, monsterToDTO } from '../../systems/monsters';
import { button, confirmBox, statChip, stickyNote, thumb, modal } from '../../../ui/components';
import { h, setChildren } from '../../../ui/dom';
import { icon } from '../../../ui/icons';
import { ensureOnline } from '../../../networking/session';
import { language, t, tn } from '../../../i18n';
import type { NotebookCtx } from './tabs';
import { bossSection } from './community';
import { ensureDrawingRules } from '../../../ui/drawingRules';

const STATUS: Record<ContestEntryDTO['status'], { stamp: string; color: string; note: string }> = {
  pending: { stamp: 'BEING CHECKED', color: 'blue', note: 'A grown-up checks every entry before other players can see it.' },
  approved: { stamp: 'IN THE CONTEST', color: 'green', note: 'Everyone can vote for it until the round ends.' },
  rejected: { stamp: 'NOT ACCEPTED', color: 'red', note: 'This one could not go in. Withdraw it and enter another drawing.' },
  winner: { stamp: 'CHAMPION', color: 'gold', note: 'It won! It lives in the Hall of Fame now.' },
};

/** Server messages are English sentences; the Thai dictionary carries them too. */
function errorText(err: unknown): string {
  if (!(err instanceof Error)) return t('Something went wrong.');
  const msg = err.message.charAt(0).toUpperCase() + err.message.slice(1);
  return t(msg.endsWith('.') || msg.endsWith('!') ? msg : `${msg}.`);
}

function closesAt(iso: string): string {
  return new Date(iso).toLocaleString(language() === 'th' ? 'th-TH' : undefined, { weekday: 'long', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
}

/** A contest monster on a paper card: drawing, name, author and stats. */
/** The drawing big, in a window (tap a contest picture). */
function showBig(ctx: NotebookCtx, e: ContestEntryDTO): void {
  const m = e.monster;
  const big = thumb(ctx.app, m.drawing, monsterArt(m) ?? 'monster:gum_blob', 320);
  big.classList.add('zoom-thumb');
  void modal(m.name, h('div', { class: 'zoom-view' }, big, h('p', { class: 'card-sub' }, t('by {name}', { name: e.authorName }))), [{ label: t('Close'), value: 'ok', variant: 'primary', color: 'yellow' }], { className: 'zoom-modal' });
}

function entryCard(ctx: NotebookCtx, e: ContestEntryDTO, ...extra: (Node | null)[]): HTMLElement {
  const m = e.monster;
  const pic = thumb(ctx.app, m.drawing, monsterArt(m) ?? 'monster:gum_blob', 84);
  return h(
    'article',
    { class: `doodle-card monster-card contest-card tier-${m.tier} ${e.voted ? 'voted' : ''}`, 'data-entry': e.id },
    h('button', { type: 'button', class: 'zoomable', title: t('See it big'), 'aria-label': t('See {name} big', { name: m.name }), on: { click: () => showBig(ctx, e) } }, pic),
    e.votes !== undefined ? h('span', { class: 'vote-count' }, '❤️ ', tn(e.votes, '{n} vote', '{n} votes')) : null,
    h('h3', null, m.name),
    h('p', { class: 'card-sub' }, t('by {name}', { name: e.authorName })),
    h('div', { class: 'chips' }, statChip('HP', m.hp), statChip('ATK', m.atk), statChip('DEF', m.def), m.luck ? statChip('LUCK', m.luck) : null),
    h('p', { class: 'skill-line' }, `★ ${t(skillDef(m.skill).name)} · ${t(RULES.personalities.find((p) => p.id === m.personality)?.name ?? '')}`),
    ...extra,
  );
}

/**
 * Notebook page for the weekly monster contest: enter one of my drawings,
 * vote for three favourites, and fight past winners in the Hall of Fame.
 */
export function contestTab(ctx: NotebookCtx): HTMLElement {
  const { app } = ctx;
  const save = app.store.state;
  const round = h('div', { class: 'contest-round' });
  const mine = h('section', { class: 'contest-mine' });
  const entries = h('section', { class: 'contest-entries' });
  const hall = h('section', { class: 'contest-hall' }, h('p', { class: 'hand-hint' }, t('Opening the Hall of Fame…')));
  let alive = true;
  ctx.onCleanup(() => (alive = false));

  const busy = async (btn: HTMLButtonElement | null, fn: () => Promise<void>): Promise<void> => {
    if (btn) btn.disabled = true;
    try {
      await fn();
    } catch (err) {
      app.toast(errorText(err), 'error');
    }
    if (alive) await load();
  };

  const renderMine = (res: ContestResponse): void => {
    const own = res.mine;
    if (own) {
      const s = STATUS[own.status];
      const withdraw =
        own.status === 'winner'
          ? null
          : button(own.status === 'rejected' ? t('Withdraw and try another') : t('Withdraw'), {
              small: true,
              variant: 'ghost',
              sfx: app,
              onClick: async (ev) => {
                if (!(await confirmBox(t('Withdraw your entry?'), t('{name} leaves this week\'s contest, and any votes for it are lost.', { name: own.monster.name }), t('Withdraw')))) return;
                await busy(ev.currentTarget as HTMLButtonElement, () => app.api.withdrawContest(own.id));
              },
            });
      setChildren(
        mine,
        h('h3', { class: 'section-title' }, t('My Entry')),
        h(
          'div',
          { class: 'contest-mine-row' },
          entryCard(ctx, own, h('span', { class: `stamp ${s.color}` }, t(s.stamp)), h('div', { class: 'card-actions' }, withdraw)),
          h('p', { class: 'hand-hint contest-status-note' }, t(s.note)),
        ),
      );
      return;
    }
    const candidates = contestCandidates(app.store.state);
    if (!candidates.length) {
      setChildren(
        mine,
        h('h3', { class: 'section-title' }, t('My Entry')),
        h('p', { class: 'hand-hint' }, t('Draw a monster in public mode to enter it. Classroom monsters are for practice only.')),
        button(t('Draw a Monster'), { color: 'yellow', sfx: app, onClick: () => void app.scenes.go('creator', undefined) }),
      );
      return;
    }
    const select = h(
      'select',
      { class: 'hand-select contest-pick', 'aria-label': t('Monster to enter') },
      candidates.map((m) => h('option', { value: m.id }, m.name)),
    );
    setChildren(
      mine,
      h('h3', { class: 'section-title' }, t('My Entry')),
      h('p', { class: 'hand-hint' }, t('Pick one of your drawings. You can enter one monster each week.')),
      h(
        'div',
        { class: 'import-row' },
        select,
        button(t('Enter the contest'), {
          small: true,
          variant: 'primary',
          color: 'yellow',
          sfx: app,
          onClick: async (ev) => {
            const m = candidates.find((c) => c.id === select.value);
            if (!m) return;
            if (!(await ensureDrawingRules(app))) return;
            const ok = await confirmBox(
              t('Enter {name}?', { name: m.name }),
              t('Everyone will see the drawing, its name and your online name after a grown-up checks it.'),
              t('Enter'),
            );
            if (!ok) return;
            await busy(ev.currentTarget as HTMLButtonElement, async () => {
              const entry = await app.api.submitContest(monsterToDTO(m));
              app.sfx.play('pop');
              app.toast(entry.status === 'approved' ? t('{name} is in the contest!', { name: m.name }) : t('{name} was sent! A grown-up will check it soon.', { name: m.name }), 'success');
            });
          },
        }),
      ),
    );
  };

  const renderEntries = (res: ContestResponse): void => {
    // My own entry is shown under My Entry; I cannot vote for it anyway.
    // Once I have voted the counts are shown: most votes first.
    const counted = res.entries.some((e) => e.votes !== undefined);
    const others = res.entries.filter((e) => !e.mine).sort((a, b) => (counted ? (b.votes ?? 0) - (a.votes ?? 0) : 0));
    const card = (e: ContestEntryDTO): HTMLElement => {
      const vote = button(e.voted ? t('Voted ✓') : t('Vote'), {
        small: true,
        variant: e.voted ? 'secondary' : 'primary',
        color: e.voted ? 'green' : 'pink',
        disabled: !e.voted && res.votesLeft === 0,
        title: e.voted ? t('Take the vote back') : res.votesLeft === 0 ? t('No votes left this week') : t('Vote for {name}', { name: e.monster.name }),
        sfx: app,
        onClick: (ev) =>
          void busy(ev.currentTarget as HTMLButtonElement, async () => {
            await app.api.vote(e.id, !e.voted);
            void load(); // fresh counts and order
            if (e.voted) return;
            let coins = 0;
            app.store.update(['inventory', 'quests'], (s) => {
              s.counters.votes++;
              coins = rewardFirstVote(s, res.round);
            });
            app.sfx.play('pop');
            app.toast(coins ? t('Vote counted! +{n} coins for your first vote this week.', { n: coins }) : t('Vote counted!'), 'success');
          }),
      });
      const report = button(t('Report'), {
        small: true,
        variant: 'ghost',
        title: t('Tell a grown-up something is not OK'),
        sfx: app,
        onClick: async (ev) => {
          if (!(await confirmBox(t('Report this monster?'), t('The grown-ups who check the contest will look at it again.'), t('Report')))) return;
          await busy(ev.currentTarget as HTMLButtonElement, async () => {
            await app.api.reportEntry(e.id);
            app.toast(t('Thanks for telling us. A grown-up will take a look.'), 'info');
          });
        },
      });
      return entryCard(ctx, e, e.voted ? h('span', { class: 'stamp green' }, t('MY VOTE')) : null, h('div', { class: 'card-actions' }, vote, report));
    };
    setChildren(
      entries,
      h('h3', { class: 'section-title' }, t('Vote for Your Favourites')),
      h('p', { class: 'hand-hint' }, counted ? t('Sorted by votes: the monster with the most votes at the end of the week wins.') : t("Vote for a drawing you like to see everyone's votes. Tap a picture to see it big.")),
      others.length ? h('div', { class: 'card-grid' }, others.map(card)) : h('p', { class: 'hand-hint' }, res.mine ? t('No other monsters in this round yet. Tell your friends!') : t('No monsters in this round yet. Be the first to enter!')),
    );
  };

  const load = async (): Promise<void> => {
    const online = app.store.state.online;
    if (!online) return;
    app.api.token = online.token;
    try {
      const res = await app.api.contest();
      if (!alive) return;
      setChildren(
        round,
        stickyNote(
          'yellow',
          h('p', null, h('b', null, t('Round {round}', { round: res.round })), ` · ${t('voting closes {when}', { when: closesAt(res.endsAt) })}`),
          h(
            'p',
            { class: 'contest-votes-left' },
            icon('star', 'inline-icon'),
            ` ${tn(res.votesLeft, '{n} vote left of {max}', '{n} votes left of {max}', { max: res.maxVotes })}`,
            app.store.state.contest.votedRounds.includes(res.round) ? null : ` · ${t('first vote: +{n} coins', { n: CONTEST_VOTE_COINS })}`,
          ),
        ),
      );
      renderMine(res);
      renderEntries(res);
    } catch (err) {
      if (alive) setChildren(round, h('p', { class: 'hand-hint' }, errorText(err)));
    }
  };

  const loadHall = async (): Promise<void> => {
    if (app.store.state.online) app.api.token = app.store.state.online.token;
    try {
      const list = await app.api.hall();
      if (!alive) return;
      let won: ContestEntryDTO[] = [];
      app.store.update(['monsters', 'inventory', 'memories'], (s) => {
        syncHall(s, list);
        won = claimChampionPrizes(s, list);
      });
      for (const e of won) app.toast(t('{name} won the contest! You got the Champion stamp.', { name: e.monster.name }), 'success');
      const fight = (e: ContestEntryDTO): void => {
        if (!app.store.hero) {
          app.toast(t('Draw a hero first — monsters need someone to fight!'), 'info');
          ctx.goTab('newhero');
          return;
        }
        void app.scenes.go('battle', { mode: 'pve', monsterId: hallMonsterId(e.id) }, 'page');
      };
      setChildren(
        hall,
        h('h3', { class: 'section-title' }, icon('trophy', 'inline-icon'), ` ${t('Hall of Fame')}`),
        h('p', { class: 'hand-hint' }, t('Every week the monster with the most votes joins the game. Anyone can fight it.')),
        list.length
          ? h(
              'div',
              { class: 'card-grid' },
              list.map((e) =>
                entryCard(
                  ctx,
                  e,
                  h('span', { class: 'stamp gold' }, t('CHAMPION')),
                  h('p', { class: 'reward-line' }, `${t('Round {round}', { round: e.round })} · ${tn(e.votes ?? 0, '{n} vote', '{n} votes')}`),
                  h('div', { class: 'card-actions' }, button(t('Fight!'), { small: true, variant: 'primary', color: 'pink', sfx: app, onClick: () => fight(e) })),
                ),
              ),
            )
          : h('p', { class: 'hand-hint' }, t("The first champion appears here when this week's round ends.")),
      );
    } catch {
      if (alive) setChildren(hall, h('p', { class: 'hand-hint' }, t('Battle server offline — the Hall of Fame is unavailable.')));
    }
  };

  if (save.online) {
    round.append(h('p', { class: 'hand-hint' }, t('Opening the contest box…')));
    void load();
  } else {
    round.append(
      stickyNote(
        'blue',
        h('p', null, t('Sign in to the battle server to enter and vote.')),
        button(t('Sign in'), {
          variant: 'primary',
          color: 'yellow',
          sfx: app,
          // Signing in changes the 'online' topic, which re-renders this page.
          onClick: () => void ensureOnline(app),
        }),
      ),
    );
  }
  void loadHall();

  return h(
    'div',
    { class: 'tab-contest' },
    h(
      'header',
      { class: 'page-header' },
      h('h2', { class: 'page-heading' }, t('Monster Contest')),
      h('p', { class: 'page-sub' }, t('Enter your best drawing, vote for your favourites, and the winner joins the game!')),
    ),
    bossSection(ctx),
    round,
    mine,
    entries,
    hall,
  );
}
